// Single-metric zoom modal (ADR-018 / SPEC-015 §3.4). A full-screen Tailwind
// overlay that re-renders the clicked card's chart at a large size. No new
// dependency — it reuses TrendChart / RadarCard with a bigger `height`.
//
// Closes on: ESC (window keydown while open), backdrop click, and the close
// button. The inner panel stops click propagation so clicks on the chart don't
// dismiss it.

import { useEffect } from "react";
import { X } from "lucide-react";
import { useI18n } from "@faceheart/i18n";
import { TrendChart } from "./TrendChart";
import { RadarCard } from "./RadarCard";
import { METRICS } from "./chartSetup";
import type { EmployeeBaseline, MeasurementSeriesResponse } from "@faceheart/api-types";

// 'radar' is the synthetic key for the health radar card; any other value is a
// METRICS line-metric key.
export const RADAR_KEY = "radar";

interface Props {
  /** The expanded card's key, or null when the modal is closed. */
  metricKey: string | null;
  onClose: () => void;
  series: MeasurementSeriesResponse["series"];
  baselineFor: (key: string) => EmployeeBaseline | undefined;
}

export function MetricModal({ metricKey, onClose, series, baselineFor }: Props) {
  const { t } = useI18n("admin");

  // ESC closes the modal while it's open. Listener is attached only when open
  // and cleaned up on close/unmount.
  useEffect(() => {
    if (metricKey === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [metricKey, onClose]);

  if (metricKey === null) return null;

  const isRadar = metricKey === RADAR_KEY;
  const meta = isRadar ? undefined : METRICS.find((m) => m.key === metricKey);
  // Unknown non-radar key: nothing to render.
  if (!isRadar && !meta) return null;

  const seriesFor = (key: string) => series.find((s) => s.metric === key);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("trends.zoom.close")}
          className="absolute -top-3 -right-3 z-10 rounded-full bg-admin-bg3 border border-admin-border p-1.5 text-admin-text2 hover:text-admin-cyan"
        >
          <X className="w-5 h-5" />
        </button>
        {isRadar ? (
          <RadarCard series={series} height="h-[70vh]" />
        ) : (
          <TrendChart
            meta={meta!}
            series={seriesFor(meta!.key)}
            baseline={meta!.hasBaseline ? baselineFor(meta!.key) : undefined}
            height="h-[70vh]"
          />
        )}
      </div>
    </div>
  );
}
